import { useEffect, useRef, useSyncExternalStore } from 'react'
import {
  LOGO_GLYPHS,
  SCROLL_INDICATORS,
  type LogoKind,
  type ScrollIndicatorKind,
} from '@/kinds/themeprofile/schema'
import { Pumpkin } from '@/components/CloseGlyph'

// Theme slots beyond the close glyph (lib/themeMarks.ts): the Shell writes `data-logo` and
// `data-scroll` on <html> from the resolved look and these read them back.

const subscribeTo = (attr: string) => (notify: () => void) => {
  const obs = new MutationObserver(notify)
  obs.observe(document.documentElement, { attributes: true, attributeFilter: [attr] })
  return () => obs.disconnect()
}
const subLogo = subscribeTo('data-logo')
const subScroll = subscribeTo('data-scroll')

export const logoFor = (root: HTMLElement): LogoKind =>
  LOGO_GLYPHS.find((k) => k === root.dataset.logo) ?? 'mark'
export const scrollIndicatorFor = (root: HTMLElement): ScrollIndicatorKind =>
  SCROLL_INDICATORS.find((k) => k === root.dataset.scroll) ?? 'none'

/** The nav logo: the theme's own mark (a pumpkin), else what the nav always showed. */
export const NavLogo = ({ size = 18, children }: { size?: number; children: React.ReactNode }) =>
  useSyncExternalStore(subLogo, () => logoFor(document.documentElement)) === 'pumpkin' ? (
    <Pumpkin size={size} />
  ) : (
    <>{children}</>
  )

/** A witch on a broomstick in a 24 box, stroked in currentColor and facing down the page. */
export const Witch = ({ size = 28 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    data-glyph="witch"
  >
    <path d="M2 17.5 21 11.5" />
    <path d="M17.5 12.6 22 9.5M17.8 14 22.5 13M18.2 15.4l4 3" />
    <path d="M8 14.8c.2-2 1.2-3.6 3-4.4" />
    <circle cx="12" cy="7.6" r="1.6" />
    <path d="M9.4 6.4h5.2M10.6 6.2 12.6 2l.8 4.2" />
  </svg>
)

/**
 * The scroll indicator a theme can ask for. iOS draws overlay scrollbars and ignores
 * ::-webkit-scrollbar, so this is drawn: a fixed rider on the right edge whose transform follows
 * the page's scroll (a passive listener, one rAF, a transform and an opacity, nothing that lays
 * out). Hidden when the page does not scroll, with reduced motion (CSS, `.scroll-witch`), and
 * between scrolls. Pointer-less, so it never takes a tap.
 */
export const ScrollIndicator = () => {
  const kind = useSyncExternalStore(subScroll, () => scrollIndicatorFor(document.documentElement))
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el || kind !== 'witch') return
    let frame = 0
    let idle = 0
    const draw = () => {
      frame = 0
      const doc = document.documentElement
      const range = doc.scrollHeight - doc.clientHeight
      if (range <= 8) {
        el.dataset.live = 'off'
        return
      }
      const track = el.parentElement?.clientHeight ?? 0
      const f = Math.min(1, Math.max(0, window.scrollY / range))
      el.style.transform = `translate3d(0, ${Math.round(f * Math.max(0, track - el.offsetHeight))}px, 0)`
      el.dataset.live = 'on'
      window.clearTimeout(idle)
      idle = window.setTimeout(() => (el.dataset.live = 'rest'), 1200)
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(draw)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll, { passive: true })
    el.dataset.live = 'rest'
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      cancelAnimationFrame(frame)
      window.clearTimeout(idle)
    }
  }, [kind])
  if (kind !== 'witch') return null
  return (
    <div className="scroll-witch-track" aria-hidden data-testid="scroll-witch">
      <div ref={ref} className="scroll-witch" data-live="rest">
        <Witch />
      </div>
    </div>
  )
}
